import { Separator } from '@/components/ui/separator';
import { Check, Download } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import clsx from 'clsx';
import { formatDate, getErrToast } from '@/utils/helpers';
import {
  enrichCareplanChronicQuestions,
  isChronicConditionQuestion,
} from '@/utils/icdChronicQuestions';
import AxiosInstance from '@/utils/AxiosInstance';
import { downloadBackendPdf } from '@/utils/downloadBackendPdf';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

const ViewCareplan = ({ carePlan, isPcm = false }: { carePlan?: any; isPcm?: boolean }) => {
  console.log({ carePlan });
  const [isDownloading, setIsDownloading] = useState(false);
  const [fetchedOrgName, setFetchedOrgName] = useState<string | null>(null);
  const [emergencyConsent, setEmergencyConsent] = useState(false);
  const [displayQuestions, setDisplayQuestions] = useState<any[] | null>(null);
  const [enrichingChronic, setEnrichingChronic] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const icdCodesForApi = useMemo(() => {
    const raw = carePlan?.icdCodes;
    const fromArray = Array.isArray(raw)
      ? raw.filter((c: unknown) => c != null && String(c).trim() !== '').map(String)
      : [];
    if (fromArray.length > 0) return fromArray;
    const single = carePlan?.icdCode;
    if (single != null && String(single).trim() !== '') return [String(single).trim()];
    return [];
  }, [carePlan?.icdCodes, carePlan?.icdCode]);

  useEffect(() => {
    const base = carePlan?.questions;
    if (!Array.isArray(base)) {
      setDisplayQuestions([]);
      return;
    }

    const chronic = base.filter(isChronicConditionQuestion);
    const hasIcdOnAllChronic =
      chronic.length > 0 &&
      chronic.every(
        (q: any) =>
          q.icdCode != null &&
          String(q.icdCode).trim() !== '' &&
          q.icdCodePart != null &&
          String(q.icdCodePart).trim() !== '',
      );

    if (!chronic.length || !icdCodesForApi.length || hasIcdOnAllChronic) {
      setEnrichingChronic(false);
      setDisplayQuestions(base);
      return;
    }

    let cancelled = false;
    setEnrichingChronic(true);
    setDisplayQuestions(null);
    (async () => {
      try {
        const enrichedChronic = await enrichCareplanChronicQuestions(
          base,
          icdCodesForApi,
        );
        if (cancelled) return;
        const others = base.filter((q: any) => !isChronicConditionQuestion(q));
        setDisplayQuestions([...others, ...enrichedChronic]);
      } catch {
        if (!cancelled) setDisplayQuestions(base);
      } finally {
        if (!cancelled) setEnrichingChronic(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [carePlan?._id, carePlan?.questions, icdCodesForApi]);

  const orgIdForFetch = useMemo(() => {
    const patient = carePlan?.patientId;
    if (!patient) return null;
    const org = patient.organizationId;
    if (typeof org === 'string' && org.trim() !== '') return org.trim();
    if (org && typeof org === 'object' && org !== null) {
      const hasName =
        (org.organizationName != null &&
          String(org.organizationName).trim() !== '') ||
        (org.name != null && String(org.name).trim() !== '');
      if (hasName) return null;
      const id = org._id;
      if (id != null && String(id).trim() !== '') return String(id).trim();
    }
    return null;
  }, [carePlan?.patientId]);

  useEffect(() => {
    setFetchedOrgName(null);
    if (!orgIdForFetch) return;
    let cancelled = false;
    AxiosInstance()
      .get<{ data?: { organizationName?: string; name?: string } }>(
        `/organizations/${orgIdForFetch}`,
      )
      .then((res) => {
        const d = res.data?.data;
        const name = d?.organizationName ?? d?.name;
        if (
          !cancelled &&
          name != null &&
          String(name).trim() !== ''
        ) {
          setFetchedOrgName(String(name).trim());
        }
      })
      .catch(() => {
        if (!cancelled) setFetchedOrgName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [orgIdForFetch]);

  const organizationDisplay = useMemo(() => {
    const patient = carePlan?.patientId;
    if (!patient) return '—';
    if (fetchedOrgName) return fetchedOrgName;

    const nameFromOrg = (o: unknown): string | null => {
      if (!o || typeof o !== 'object') return null;
      const rec = o as Record<string, unknown>;
      const n = rec.organizationName ?? rec.name;
      if (n == null || String(n).trim() === '') return null;
      return String(n).trim();
    };

    const fromPatientOrg = nameFromOrg(patient.organizationId);
    if (fromPatientOrg) return fromPatientOrg;

    const fromPatientAlt = nameFromOrg(patient.organization);
    if (fromPatientAlt) return fromPatientAlt;

    const fromCarePlanOrg = nameFromOrg(carePlan?.organizationId);
    if (fromCarePlanOrg) return fromCarePlanOrg;

    const fac = patient.facilityId;
    if (fac && typeof fac === 'object') {
      const fn = (fac as { facilityName?: string; name?: string }).facilityName ??
        (fac as { facilityName?: string; name?: string }).name;
      if (fn != null && String(fn).trim() !== '') return String(fn).trim();
    }

    return '—';
  }, [carePlan?.patientId, carePlan?.organizationId, fetchedOrgName]);

  if (!carePlan?.patientId) {
    return null;
  }

  const patient = carePlan.patientId;
  const caretaker = carePlan.careTakerId;
  const generatedDate = formatDate(carePlan.carePlanDate);
  const dob = formatDate(patient.dateOfBirth);
  const patientName = [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' ');
  const caretakerName = caretaker
    ? [caretaker.firstName, caretaker.middleName, caretaker.lastName].filter(Boolean).join(' ') || '—'
    : '—';
  const icdCodesDisplay = (() => {
    const raw = carePlan.icdCodes;
    const fromArray = Array.isArray(raw)
      ? raw.filter((c: unknown) => c != null && String(c).trim() !== '').map((c: unknown) => String(c).trim())
      : raw != null && String(raw).trim() !== ''
        ? [String(raw).trim()]
        : [];
    if (fromArray.length > 0) return fromArray.join(', ');
    const single = carePlan.icdCode;
    if (single != null && String(single).trim() !== '') return String(single).trim();
    return '';
  })();
  const questionsToRender = displayQuestions ?? carePlan?.questions ?? [];

  if (enrichingChronic && displayQuestions === null) {
    return (
      <div className="bg-gray-100 min-h-screen py-8 px-4 flex items-center justify-center">
        <p className="text-sm text-gray-600 teachers">Loading care plan questions…</p>
      </div>
    );
  }

  const questionCategory = (q: any) => {
    const raw = q?.category;
    if (raw == null || String(raw).trim() === '') return '';
    return String(raw);
  };
  const categories: string[] = [];
  const catSet = new Set<string>();
  questionsToRender.forEach((q: any) => {
    const category = questionCategory(q);
    if (!catSet.has(category)) {
      catSet.add(category);
      categories.push(category);
    }
  });
  const renderQuestion = (q: any) => {
    // Check if answer doesn't match any option (meaning "other" was selected with custom text)
    const hasOptions = q.options && Array.isArray(q.options) && q.options.length > 0 && q.options.some((opt: string) => opt.trim() !== '');
    let isOtherSelected = false;
    let customAnswer = '';
    
    if (hasOptions && q.answer) {
      const validOptions = q.options.filter((opt: string) => opt.trim() !== '').map((opt: string) => opt.trim());
      
      if (q.type === 'multiple_choice') {
        if (Array.isArray(q.answer)) {
          // Check if any answer doesn't match options
          // const nonMatching = q.answer.filter((ans: any) => !validOptions.includes(String(ans).trim()));
          // if (nonMatching.length > 0) {
          //   isOtherSelected = true;
          //   customAnswer = nonMatching.map((ans: any) => String(ans).trim()).join(', ');
          // }
        } else {
          // Answer is a string but type is multiple_choice (custom text entered)
          const answerStr = String(q.answer).trim();
          if (answerStr && !validOptions.includes(answerStr)) {
            isOtherSelected = true;
            customAnswer = answerStr;
          }
        }
      } else if (q.type === 'single_choice' || !q.type) {
        // For single choice, check if answer doesn't match any option
        const answerStr = String(q.answer).trim();
        if (answerStr && !validOptions.includes(answerStr)) {
          isOtherSelected = true;
          customAnswer = answerStr;
        }
      }
    }
    
    // Check if "other" option exists in the options
    const otherOptionExists = hasOptions && q.options.some((opt: string) => opt.trim().toLowerCase() === 'other');
    
    const commentText = typeof q?.comment === 'string' ? q.comment.trim() : '';

    return (
      <div key={q.questionrandomid} className="mb-4">
        <div className="teachers text-xs mb-2.5">{q.question}</div>
        {hasOptions ? (
          <div className=" space-y-1">
            {q.options
              .filter((opt: string) => opt.trim() !== '')
              .map((opt: string, idx: number) => {
                const optTrimmed = opt.trim();
                const isSelected =
                  q.type === 'multiple_choice'
                    ? Array.isArray(q.answer) && q.answer.some((ans: any) => String(ans).trim() === optTrimmed)
                    : String(q.answer).trim() === optTrimmed;
                
                // Mark "other" as selected if custom answer was entered
                const isOtherOpt = optTrimmed.toLowerCase() === 'other';
                const showAsSelected = isSelected || (isOtherOpt && isOtherSelected);
                
                return (
                  <div key={idx} className="flex items-center">
                    <span
                      className={` font-medium teachers text-xs ${
                        showAsSelected ? '' : ''
                      }`}
                    >
                      {opt}
                    </span>
                    {showAsSelected && <span className="ml-2 text-green-500 text-xs"><Check width={14} height={14}/></span>}
                  </div>
                );
              })}
            {/* Show "other" option if it doesn't exist in options but custom answer was entered */}
            {isOtherSelected && !otherOptionExists && (
              <div className="flex items-center">
                <span className="font-medium teachers text-xs">Other</span>
                <span className="ml-2 text-green-500 text-xs"><Check width={14} height={14}/></span>
              </div>
            )}
            {/* Show custom answer text if "other" was selected */}
            {isOtherSelected && customAnswer && (
              <div className="ml-6 mt-1">
                <span className="font-medium teachers text-xs text-gray-600 italic">
                  {customAnswer}
                </span>
              </div>
            )}
          </div>
        ) : (
          q.answer && (
            <div className="font-medium teachers text-xs ">
              {q.answer}
            </div>
          )
        )}
        {commentText && (
          <div className="mt-1.5">
            <span className="teachers text-[11px] text-gray-500">Comment:</span>
            <p className="teachers text-xs text-gray-700">{commentText}</p>
          </div>
        )}
        <Separator className='bg-[#dddddd] my-2'/>
      </div>
    );
  };
  const sectionTitle = (cat: string) => {
    if (!cat) return 'Care Plan';
    return cat
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  const chronicIcdKey = (q: any): string => {
    if (q.icdCodePart != null && String(q.icdCodePart).trim() !== '') {
      return String(q.icdCodePart).trim();
    }
    if (typeof q.icdCode === 'string' && q.icdCode.includes(' - ')) {
      return q.icdCode.split(' - ')[0].trim();
    }
    if (typeof q.icdCode === 'string' && q.icdCode.trim() !== '') {
      return q.icdCode.trim();
    }
    return 'Other';
  };
  const handleDownloadPdf = async () => {
    const id = carePlan?._id;
    if (!id) return;
    setIsDownloading(true);
    try {
      await downloadBackendPdf({
        url: isPcm ? `/pcm-careplan/${id}/pdf` : `/careplan/${id}/pdf`,
        fallbackFilename: 'care-plan.pdf',
      });
    } catch (err) {
      getErrToast(err);
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4">
        <div className='flex justify-end max-w-[595px] mx-auto mb-4'>

      <Button
        variant="outline"
        id='pdfDownload'
        className="w-full sm:w-auto hidden"
        onClick={handleDownloadPdf}
        disabled={isDownloading}
        >
        <Download className="w-4 h-4 mr-2" />
        {isDownloading ? "Preparing PDF..." : "PDF"}
      </Button>
          </div>
      <div ref={contentRef} className={clsx ("bg-white shadow-lg md:p-[40px] p-4")} style={{ 
        width: '595px', 
        maxWidth: '100%', 
        margin: '0 auto', 
        boxSizing: 'border-box',
        // padding: '40px',
        minHeight: '842px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        <div className="header mb-8 bg-[#eeeeee] p-5 rounded-md ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">PATIENT NAME:</span>
              <span className="ml-2 text-black text-sm teachers">{patientName}</span>
            </div>
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">DATE OF BIRTH:</span>
              <span className="ml-2 text-black text-sm teachers">{dob}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">CARETAKER:</span>
              <span className="ml-2 text-black text-sm teachers">{caretakerName}</span>
            </div>
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">Creation Date:</span>
              <span className="ml-2 text-black text-sm teachers">{generatedDate}</span>
            </div>
            {/* <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">ICD CODES:</span>
              <span className="ml-2 text-black text-sm teachers">
                {carePlan?.icdCodes?.length
                  ? carePlan.icdCodes.map((code: string, idx: number) => (
                      <span key={`${code}-${idx}`} className="block shrink-0">
                        {code}
                      </span>
                    ))
                  : '—'}
              </span>
            </div> */}
          </div>
          <div className="mt-4 pt-3 border-t border-[#dddddd] space-y-3">
            {icdCodesDisplay ? (
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">ICD CODES:</span>
                <span className="ml-2 text-black text-sm teachers">{icdCodesDisplay}</span>
              </div>
            ) : null}
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">ORGANIZATION:</span>
              <span className="ml-2 text-black text-sm teachers">{organizationDisplay}</span>
            </div>
          </div>
        </div>
        {/* (console.log(carePlan)) */}
        {categories.map((cat) => {
          const sectionQuestions = questionsToRender.filter(
            (q: any) => questionCategory(q) === cat,
          );
          const hasIcdMeta = sectionQuestions.some(
            (q: any) => q.icdCodePart != null || q.icdCode != null,
          );

          if (cat === 'Chronic Conditions' && hasIcdMeta) {
            const byIcd = sectionQuestions.reduce((acc: Map<string, any[]>, q: any) => {
              const key = chronicIcdKey(q);
              if (!acc.has(key)) acc.set(key, []);
              acc.get(key)!.push(q);
              return acc;
            }, new Map<string, any[]>());

            const icdGroups = (Array.from(byIcd.entries()) as [string, any[]][]).sort(
              ([a], [b]) => a.localeCompare(b, undefined, { numeric: true }),
            );

            return (
              <div key={cat} className="mb-3">
                <h3 className="font-medium teachers mb-2.5">{sectionTitle(cat)}</h3>
                <div className="space-y-6">
                  {icdGroups.map(([part, qs]) => (
                    <div
                      key={part}
                      className="rounded-lg border border-[#e5e5e5] bg-[#fafafa] p-4"
                    >
                      <div className="mb-3 border-b border-[#dddddd] pb-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E6E6E] teachers">
                          ICD
                        </p>
                        <p className="text-sm font-semibold teachers text-black">{part}</p>
                        {qs[0]?.icdCode && qs[0].icdCode !== part && (
                          <p className="text-xs text-gray-600 teachers mt-1">{qs[0].icdCode}</p>
                        )}
                      </div>
                      {qs.map(renderQuestion)}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <div key={cat} className="mb-3">
              <h3 className="font-medium teachers mb-2.5">
                {sectionTitle(cat)}
              </h3>
              {sectionQuestions.map(renderQuestion)}
            </div>
          );
        })}
         {/* Emergency service consent */}
         <div className="p-4 border rounded-xl space-y-3 bg-muted/30">
                    <p className="text-sm text-muted-foreground">
                      We are not an emergency service, so at any point you feel you need
                      to seek medical attention immediately, we advise you to call 911.
                    </p>
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="emergencyConsent"
                        checked={true}
                        onCheckedChange={() => {}}
                      />
                      <Label
                        htmlFor="emergencyConsent"
                        className="text-xs text-gray-700 leading-5"
                      >
                        I understand that Anthro Care is not an emergency service and I
                        will call 911 if I need immediate medical attention.
                      </Label>
                    </div>
                  </div>
      </div>
    </div>
  );
};

export default ViewCareplan;